/* product-inventory.js
 * Splits inventoryPolicy === 'continue' and count <= 0 into:
 * - preorder      => by SELLING PLAN ID lookup
 * - custom_order  => by SELLING PLAN ID lookup (Custom)
 *
 * ADDITION (HTS only):
 * If product tag includes "home theater seating" (data-is-hts="true"):
 * Use variant metafields from variant-data JSON:
 *  - out_of_stock_status
 *  - eta_delivery_text
 *
 * HTS rules (only when count <= 0 and inventoryPolicy === 'continue'):
 * 1) if out_of_stock_status === 'custom order' => Custom Order HTS
 * 2) else if out_of_stock_status blank AND eta_delivery_text filled => Pre Order w/ETA HTS
 * 3) else if out_of_stock_status blank AND eta_delivery_text blank  => Pre Order w/o ETA HTS
 *
 * IMPORTANT:
 * This file does NOT rely on per-variant selling_plan_id in variant JSON.
 * It derives it by looking up the current variant_id inside window.STOQ_PLANS
 * (shop metafield selling_plans JSON), if available.
 *
 * Messaging mapping (UNCHANGED):
 * - preorder      => this.dataset.textNoStockBackordered   (PRE ORDER / 15% message)
 * - custom_order  => this.dataset.textNoStock              (CUSTOM ORDER / 50% message)
 */

if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Determine initial variant
      const initialVariantId =
        this.closest('.product-info')?.querySelector('form [name="id"]')?.value ||
        this.closest('form')?.querySelector('[name="id"]')?.value;

      const initialVariantData = this.variantInventory?.find(
        (v) => String(v.id) === String(initialVariantId)
      );

      const initialSellingPlanId = this.getSellingPlanIdForVariant(initialVariantId);

      // Init inventory UI
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        initialSellingPlanId,
        initialVariantData || null
      );

      // Sync ETA property on init
      this.syncEtaProperty();
    }

    /**
     * Gets the inventory data for all product variants
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Returns Shopify selling plan id for a variant by scanning window.STOQ_PLANS.
     */
    getSellingPlanIdForVariant(variantId) {
      if (!variantId) return 0;

      const plans = window.STOQ_PLANS;
      if (!Array.isArray(plans)) return 0;

      const vIdStr = String(variantId);

      const plan = plans.find((sp) => {
        if (!sp || !sp.enabled) return false;
        if (!Array.isArray(sp.variant_ids)) return false;
        return sp.variant_ids.some((id) => String(id) === vIdStr);
      });

      const spid = plan?.shopify_selling_plan_id;
      return spid ? Number(spid) : 0;
    }

    /**
     * Copies #eta-text into hidden line-item property input #eta-property.
     */
    syncEtaProperty() {
      setTimeout(() => {
        const etaEl = document.getElementById('eta-text');
        const etaProp = document.getElementById('eta-property');
        if (etaEl && etaProp) {
          etaProp.value = (etaEl.textContent || '').trim();
        }
      }, 0);
    }

    /**
     * Handles variant change
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];

        const sellingPlanId = this.getSellingPlanIdForVariant(evt.detail.variant.id);

        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          sellingPlanId,
          inventory || null
        );

        this.syncEtaProperty();
      } else {
        this.updateInventory(0, false, 'deny', 0, null);
        this.hidden = true;
        this.syncEtaProperty();
      }
    }

    /**
     * Updates the inventory notice
     */
    updateInventory(count, available, inventoryPolicy, sellingPlanId = 0, variantData = null) {
      if (count === 0 && !available && this.hidden) return;

      let inventoryLevel;
      let specialLabel = '';

      const isHTS = String(this.dataset.isHts || '').toLowerCase() === 'true';

      // --- MAIN LOGIC: Only touch preorder/custom. Leave others untouched.
      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          // =========================
          // HTS OVERRIDE (ONLY HTS)
          // =========================
          if (isHTS && variantData) {
            const outOfStockStatus = String(variantData.out_of_stock_status || '').trim().toLowerCase();
            const etaDeliveryText = String(variantData.eta_delivery_text || '').trim();

            if (outOfStockStatus === 'custom order') {
              inventoryLevel = 'custom_order';
              specialLabel = 'Custom Order';
            } else if (!outOfStockStatus && etaDeliveryText) {
              inventoryLevel = 'preorder';
              specialLabel = 'Pre Order';
            } else if (!outOfStockStatus && !etaDeliveryText) {
              inventoryLevel = 'preorder';
              specialLabel = 'Pre Order';
            } else {
              // If something unexpected, keep safe fallback (continue selling should NOT show sold out)
              inventoryLevel = 'preorder';
              specialLabel = 'Pre Order';
            }
          } else {
            // =========================
            // NON-HTS: Selling plan mapping (existing logic)
            // =========================
            const spid = Number(sellingPlanId) || 0;

            if (spid === 3846668457) {
              inventoryLevel = 'preorder';
              specialLabel = 'Pre Order';
            } else if (spid === 4103602345) {
              inventoryLevel = 'preorder';
              specialLabel = 'Pre Order';
            } else if (spid === 3846701225) {
              inventoryLevel = 'custom_order';
              specialLabel = 'Custom Order';
            } else {
              // IMPORTANT: continue selling should NOT show SOLD OUT
              // If no matching selling plan found:
              inventoryLevel = 'preorder';
              specialLabel = 'Custom Order Only';
            }
          }
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      const showNoticeGate =
        (this.dataset.showNotice === 'always'
          || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(
          (inventoryLevel === 'preorder')
          && this.dataset.showNoStockBackordered === 'false'
        );

      if (showNoticeGate) {
        this.hidden = false;
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Label rendering (only special cases changed)
        if (inventoryLevel === 'preorder') {
          this.inventoryNotice.innerText = specialLabel || 'Pre-Order';
        } else if (inventoryLevel === 'custom_order') {
          this.inventoryNotice.innerText = specialLabel || 'Custom Order';
        } else if (
          inventoryLevel !== 'in_stock'
          && (this.dataset.showCount === 'always'
            || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))
        ) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Urgency message (unchanged mapping)
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'preorder') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'custom_order') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Indicator bar (unchanged)
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          const isSpecialOutOfStock = (inventoryLevel === 'preorder' || inventoryLevel === 'custom_order');

          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && !isSpecialOutOfStock) {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }

          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
